import { useState, useEffect, useCallback, UIEvent } from "react";
import { Button, IconButton } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import Checkbox from "@mui/material/Checkbox";

import "../file-table/style.scss";
import { ReactComponent as SmilePlaceholder } from "../../../assets/placeholder--sad-smile.icon.svg";
import { ReactComponent as UploadIcon } from "../../../assets/cloud--upload.icon.svg";
import { ReactComponent as CheckboxIcon } from "../../../assets/checkbox.icon.svg";
import { ReactComponent as CheckboxCheckedIcon } from "../../../assets/checkbox-checked.icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/delete.icon.svg";
import {
    LearningObjectStatus,
    TrialLearningObjectStatus
} from "../../../common/enums/learning-object-status.enum";
import { LearningObject } from "../../../common/interfaces/admin/learning-object.interface";
import { RecognitionResult } from "../../../common/interfaces/admin/recognition-result.interface";
import {
    RecognitionResultStatus,
    RRSwitchStatus
} from "../../../common/enums/recognition-result-status.enum";
import { Loader } from "../../loader/loader.comp";
import { SelectionFilter } from "../../../components/menu-filter/menu-filter.comp";

const TrialLearningObjects = ({
    maxTrialFileCount = 10,
    totalCount,
    learningObjects,
    checkedLOs,
    setCheckedLOs,
    handleFilesUpload,
    handleAppendLOs,
    handleDelete,
    handleAIResult,
    handleChangePublishStatus
}: {
    maxTrialFileCount?: number;
    totalCount?: number;
    learningObjects: LearningObject[];
    checkedLOs: LearningObject[];
    setCheckedLOs: (arg1: LearningObject[]) => void;
    handleFilesUpload: () => void;
    handleAppendLOs?: () => void;
    handleDelete?: (arg1: string[]) => void;
    handleAIResult?: (arg1: string) => void;
    handleChangePublishStatus: (arg1: RecognitionResult, arg2: string) => void;
}) => {
    const scrollDelata = 250;
    const [appendAvailable, setAppendAvailable] = useState<boolean>(true);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [rrStatusList] = useState<string[]>(Object.values(RRSwitchStatus));

    useEffect(() => {
        setShowLoader(!!handleAppendLOs && !!totalCount && totalCount !== learningObjects.length);
    }, [learningObjects, totalCount, handleAppendLOs]);

    const handleChangeSelectAll = useCallback(() => {
        setCheckedLOs(learningObjects.length === checkedLOs.length ? [] : learningObjects);
    }, [learningObjects, checkedLOs, setCheckedLOs]);

    const handleChangeSelect = useCallback(
        (item: LearningObject) => {
            const newFiles = [...checkedLOs];
            const index = newFiles.indexOf(item);
            index !== -1 ? newFiles.splice(index, 1) : newFiles.push(item);
            setCheckedLOs(newFiles);
        },
        [checkedLOs, setCheckedLOs]
    );

    const handleAIResultClick = (item: LearningObject) => {
        if (handleAIResult && item.recognitionResults.length) {
            handleAIResult(item.recognitionResults[0].uid);
        }
    };

    const handleDeleteClick = (item: LearningObject) => {
        if (handleDelete) {
            handleDelete([item.loItemId]);
        }
    };

    const fileStatusColor = (status: string) => {
        switch (status as LearningObjectStatus) {
            case LearningObjectStatus.inUse:
                return "green";
            case LearningObjectStatus.idle:
            case LearningObjectStatus.converting:
            case LearningObjectStatus.recognizing:
            case LearningObjectStatus.transcribing:
            case LearningObjectStatus.waitingForDownload:
            case LearningObjectStatus.waitingForRecognition:
                return "yellow";
            case LearningObjectStatus.error:
                return "red";
            default:
                return "default";
        }
    };

    const fileStatusName = (status: string) => {
        switch (status as LearningObjectStatus) {
            case LearningObjectStatus.inUse:
                return TrialLearningObjectStatus.ready;
            case LearningObjectStatus.idle:
                return TrialLearningObjectStatus.idle;
            case LearningObjectStatus.converting:
            case LearningObjectStatus.recognizing:
            case LearningObjectStatus.transcribing:
            case LearningObjectStatus.waitingForDownload:
            case LearningObjectStatus.waitingForRecognition:
                return TrialLearningObjectStatus.waiting;
            case LearningObjectStatus.error:
                return TrialLearningObjectStatus.error;
            default:
                return status;
        }
    };

    const isAIResultAvailable = (item: LearningObject) => {
        if (!item.recognitionResults.length) return false;

        const status = item.recognitionResults[0].status;

        return (
            status === RecognitionResultStatus.active ||
            status === RecognitionResultStatus.published ||
            status === RecognitionResultStatus.recognized ||
            status === RecognitionResultStatus.generatingSummary ||
            status === RecognitionResultStatus.waitingForSummary
        );
    };

    const getCurrentRR = (item: LearningObject) => {
        if (!item.recognitionResults.length) return null;

        return item.recognitionResults.find(rr => rr.isPublish) ?? item.recognitionResults[0];
    };

    const changePublishStatus = (status: string, loName: string, rr: RecognitionResult | null) => {
        if (!rr) return;

        if (rr.isPublish !== (status === RRSwitchStatus.published)) {
            handleChangePublishStatus(rr, loName);
        }
    };

    const handleListScroll = (event: UIEvent<HTMLElement>) => {
        if (!handleAppendLOs || (totalCount && totalCount === learningObjects.length)) return;

        const scrollBottom =
            event.currentTarget.scrollHeight -
            (event.currentTarget.scrollTop + event.currentTarget.offsetHeight);

        if (appendAvailable && scrollBottom < scrollDelata) {
            setAppendAvailable(false);
            handleAppendLOs();
        } else if (scrollBottom >= scrollDelata) {
            setAppendAvailable(true);
        }
    };

    return (
        <div className="file-table-view">
            {learningObjects.length > 0 ? (
                <div className="file-table" role="table" aria-label="Trial files">
                    <div className="file-table-header" role="row">
                        <div
                            className="file-table-header-cell file-table-cell__select"
                            role="columnheader"
                        >
                            <Checkbox
                                checked={learningObjects.length === checkedLOs.length}
                                onChange={handleChangeSelectAll}
                                icon={<CheckboxIcon />}
                                checkedIcon={<CheckboxCheckedIcon />}
                                inputProps={{ "aria-label": "Select all files" }}
                            />
                        </div>
                        <div
                            className="file-table-header-cell file-table-cell__name"
                            role="columnheader"
                        >
                            File Name
                        </div>
                        <div
                            className="file-table-header-cell file-table-cell__status"
                            role="columnheader"
                        >
                            Status
                        </div>
                        <div
                            className="file-table-header-cell file-table-cell__rr-status"
                            role="columnheader"
                        >
                            RR Status
                        </div>
                        <div
                            className="file-table-header-cell file-table-cell__result"
                            role="columnheader"
                        ></div>
                        <div
                            className="file-table-header-cell file-table-cell__delete"
                            role="columnheader"
                        ></div>
                    </div>
                    <div className="file-table-body" onScroll={handleListScroll} role="rowgroup">
                        {learningObjects.map(item => (
                            <div key={item.loItemId} className="file-table-row" role="row">
                                <div
                                    className="file-table-cell file-table-cell__select"
                                    role="cell"
                                >
                                    <Checkbox
                                        checked={checkedLOs.includes(item)}
                                        onChange={() => handleChangeSelect(item)}
                                        icon={<CheckboxIcon />}
                                        checkedIcon={<CheckboxCheckedIcon />}
                                        inputProps={{ "aria-label": `Select ${item.name}` }}
                                    />
                                </div>
                                <div
                                    className="file-table-cell file-table-cell__name"
                                    role="rowheader"
                                >
                                    <span className="table-file-name">{item.name}</span>
                                </div>
                                <div
                                    className="file-table-cell file-table-cell__status"
                                    role="cell"
                                >
                                    <div
                                        className={`table-file-status table-file-status__${fileStatusColor(item.status)}`}
                                    >
                                        <div className="table-file-status__circle"></div>
                                        <span className="table-file-status__title">
                                            {fileStatusName(item.status)}
                                        </span>
                                    </div>
                                </div>
                                <div
                                    className="file-table-cell file-table-cell__rr-status"
                                    role="cell"
                                >
                                    <SelectionFilter
                                        list={rrStatusList}
                                        disabled={!item.recognitionResults.length}
                                        selectedItem={
                                            getCurrentRR(item)?.isPublish
                                                ? RRSwitchStatus.published
                                                : RRSwitchStatus.off
                                        }
                                        setSelectedItem={status =>
                                            changePublishStatus(
                                                status,
                                                item.name,
                                                getCurrentRR(item)
                                            )
                                        }
                                        rightPosition={true}
                                    />
                                </div>
                                <div
                                    className="file-table-cell file-table-cell__result"
                                    role="cell"
                                >
                                    <Button
                                        className="table-file__button-result"
                                        variant="outlined"
                                        disabled={!isAIResultAvailable(item)}
                                        onClick={() => handleAIResultClick(item)}
                                    >
                                        {isAIResultAvailable(item) ? "AI Result" : "Loading"}
                                    </Button>
                                </div>
                                <div
                                    className="file-table-cell file-table-cell__delete"
                                    role="cell"
                                >
                                    <IconButton
                                        className="table-file__button-delete"
                                        onClick={() => handleDeleteClick(item)}
                                    >
                                        <SvgIcon component={DeleteIcon} inheritViewBox />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                        {showLoader && (
                            <div className="file-table-row file-table-row__loader">
                                <Loader size={24} borderWidth={4} />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="file-table-placeholder">
                    <div className="file-table-placeholder-content">
                        <SvgIcon
                            className="file-table-placeholder-content__icon"
                            component={SmilePlaceholder}
                            inheritViewBox
                        />
                        <span className="file-table-placeholder-content__title">So empty</span>
                        <span className="file-table-placeholder-content__description">
                            To use this wise AI assistant, give it something to think about. Upload
                            your documents, and it will analyze them to answer all your questions.
                            Enjoy!
                        </span>
                        <span className="file-table-placeholder-content__description--warning">
                            {`Note: In the trial version you can upload up to ${maxTrialFileCount} files (up to 100 MB each)`}
                        </span>
                        <Button
                            className="primary-button--icon-first file-table-placeholder-content__button"
                            variant="text"
                            startIcon={
                                <SvgIcon
                                    className="primary-button--icon-first__icon"
                                    component={UploadIcon}
                                    inheritViewBox
                                />
                            }
                            onClick={handleFilesUpload}
                        >
                            <span className="primary-button--icon-first__text">Upload File(s)</span>
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrialLearningObjects;
